/**
 * Permission Utilities - Các hàm kiểm tra quyền truy cập
 */

/**
 * Kiểm tra quyền truy cập đơn lẻ
 * @param {Array} listPermission - Danh sách quyền của user
 * @param {string|Array} condition - Điều kiện quyền cần kiểm tra
 * @returns {boolean} - true nếu có quyền, false nếu không
 */
export const checkPermission = (listPermission, condition) => {
  if (!listPermission || !Array.isArray(listPermission)) {
    return false;
  }

  if (Array.isArray(condition)) {
    return condition.some((perm) => listPermission.includes(perm));
  }

  return listPermission.includes(condition);
};

/**
 * Kiểm tra nhiều quyền cùng lúc (AND logic)
 * @param {Array} listPermission - Danh sách quyền của user
 * @param {Array} conditions - Mảng các điều kiện quyền
 * @returns {boolean} - true nếu có tất cả quyền, false nếu thiếu bất kỳ quyền nào
 */
export const checkAllPermissions = (listPermission, conditions) => {
  if (
    !listPermission ||
    !Array.isArray(listPermission) ||
    !Array.isArray(conditions)
  ) {
    return false;
  }

  return conditions.every((condition) => listPermission.includes(condition));
};

/**
 * Kiểm tra quyền với OR logic (chỉ cần có 1 trong các quyền)
 * @param {Array} listPermission - Danh sách quyền của user
 * @param {Array} conditions - Mảng các điều kiện quyền
 * @returns {boolean} - true nếu có ít nhất 1 quyền, false nếu không có quyền nào
 */
export const checkAnyPermission = (listPermission, conditions) => {
  if (
    !listPermission ||
    !Array.isArray(listPermission) ||
    !Array.isArray(conditions)
  ) {
    return false;
  }

  return conditions.some((condition) => listPermission.includes(condition));
};

/**
 * Kiểm tra quyền với custom logic
 * @param {Array} listPermission - Danh sách quyền của user
 * @param {Function} checkFunction - Hàm kiểm tra custom
 * @returns {boolean} - Kết quả của hàm kiểm tra
 */
export const checkCustomPermission = (listPermission, checkFunction) => {
  if (
    !listPermission ||
    !Array.isArray(listPermission) ||
    typeof checkFunction !== 'function'
  ) {
    return false;
  }

  return checkFunction(listPermission);
};

/**
 * Lấy danh sách quyền từ localStorage
 * @param {string} key - Key lưu trữ trong localStorage (mặc định: 'listPermission')
 * @returns {Array} - Danh sách quyền
 */
export const getPermissionsFromStorage = (key = 'listPermission') => {
  try {
    const permissions = localStorage.getItem(key);
    return permissions ? JSON.parse(permissions) : [];
  } catch (error) {
    console.error('Error getting permissions from storage:', error);
    return [];
  }
};

/**
 * Lưu danh sách quyền vào localStorage
 * @param {Array} permissions - Danh sách quyền
 * @param {string} key - Key lưu trữ trong localStorage (mặc định: 'listPermission')
 */
export const savePermissionsToStorage = (
  permissions,
  key = 'listPermission',
) => {
  try {
    localStorage.setItem(key, JSON.stringify(permissions));
  } catch (error) {
    console.error('Error saving permissions to storage:', error);
  }
};

/**
 * Kiểm tra quyền với regex pattern
 * @param {Array} listPermission - Danh sách quyền của user
 * @param {string|RegExp} pattern - Pattern để kiểm tra
 * @returns {boolean} - true nếu có quyền match pattern
 */
export const checkPermissionPattern = (listPermission, pattern) => {
  if (!listPermission || !Array.isArray(listPermission)) {
    return false;
  }

  const regex = pattern instanceof RegExp ? pattern : new RegExp(pattern);
  return listPermission.some((permission) => regex.test(permission));
};

/**
 * Lọc danh sách quyền theo pattern
 * @param {Array} listPermission - Danh sách quyền của user
 * @param {string|RegExp} pattern - Pattern để lọc
 * @returns {Array} - Danh sách quyền đã lọc
 */
export const filterPermissionsByPattern = (listPermission, pattern) => {
  if (!listPermission || !Array.isArray(listPermission)) {
    return [];
  }

  const regex = pattern instanceof RegExp ? pattern : new RegExp(pattern);
  return listPermission.filter((permission) => regex.test(permission));
};

/**
 * Kiểm tra quyền với prefix
 * @param {Array} listPermission - Danh sách quyền của user
 * @param {string} prefix - Prefix cần kiểm tra
 * @returns {boolean} - true nếu có quyền bắt đầu với prefix
 */
export const checkPermissionPrefix = (listPermission, prefix) => {
  if (!listPermission || !Array.isArray(listPermission) || !prefix) {
    return false;
  }

  return listPermission.some((permission) => permission.startsWith(prefix));
};

/**
 * Lấy tất cả quyền có prefix
 * @param {Array} listPermission - Danh sách quyền của user
 * @param {string} prefix - Prefix cần lọc
 * @returns {Array} - Danh sách quyền có prefix
 */
export const getPermissionsByPrefix = (listPermission, prefix) => {
  if (!listPermission || !Array.isArray(listPermission) || !prefix) {
    return [];
  }

  return listPermission.filter((permission) => permission.startsWith(prefix));
};

export default {
  checkPermission,
  checkAllPermissions,
  checkAnyPermission,
  checkCustomPermission,
  getPermissionsFromStorage,
  savePermissionsToStorage,
  checkPermissionPattern,
  filterPermissionsByPattern,
  checkPermissionPrefix,
  getPermissionsByPrefix,
};
